import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../components/Button";
import Container from "../components/Container";
import InfoModal from "../components/InfoModal";
import LoadingCard from "../components/LoadingCard";
import FilterTariffsModal from "../components/Quotes/FilterTariffsModal";
import PriceCapInfoModal from "../components/Quotes/PriceCapInfoModal";
import QuotesHeader from "../components/Quotes/QuotesHeader";
import TariffPreview from "../components/Quotes/TariffPreview";
import { useTariff } from "../context/TariffContext";
import { useUserInfo } from "../context/UserInfoContext";
import EN from "../locales/en.json";
import interpolate from "../utils/interpolate";

const Quotes = () => {
  const navigate = useNavigate();
  const { mpan, mprn } = useParams();

  useEffect(() => {
    if (!mpan) {
      navigate("/address");
    }
  }, []);

  const [quotes, setQuotes] = useState([]);
  const [fetchingQuotes, setFetchingQuotes] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [showHowSavingsCalculatedModal, setShowHowSavingsCalculatedModal] =
    useState(false);
  const [filters, setFilters] = useState({
    onlyShowSwitchable: true,
    contractTypes: [],
    paymentTypes: [],
  });
  const { getQuotes, userDefinedTariffInfo } = useTariff();
  const { hasSeenPriceCapInfoModal, setHasSeenPriceCapInfoModal } =
    useUserInfo();
  const [showInfoModal, setShowInfoModal] = useState(!hasSeenPriceCapInfoModal);

  // Price Cap Tariff Modal
  useEffect(() => {
    if (showInfoModal) {
      setHasSeenPriceCapInfoModal(true);
    }
  }, [showInfoModal]);

  const fetchData = async () => {
    if (mpan) {
      setFetchingQuotes(true);
      const quotes = await getQuotes(mpan, mprn);
      setQuotes(quotes);
      setFetchingQuotes(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [mpan, userDefinedTariffInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredQuotes = quotes?.filter((quote) => {
    // Filter for switchable tariffs
    if (filters.onlyShowSwitchable && !quote.isSwitchable) {
      return false;
    }

    // Filter for contract types
    if (
      filters.contractTypes.length > 0 &&
      !filters.contractTypes.includes(quote.tariffType)
    ) {
      return false;
    }

    // Filter for payment types
    if (filters.paymentTypes.length > 0) {
      const paymentType = quote.tilPayTypeElec.toLowerCase();
      const matchingPaymentType = filters.paymentTypes.find(
        (type) => paymentType === type.toLowerCase()
      );
      if (!matchingPaymentType) {
        return false;
      }
    }

    return true;
  });

  return (
    <Container>
      <QuotesHeader
        setShowHowSavingsCalculatedModal={setShowHowSavingsCalculatedModal}
      />

      {!fetchingQuotes && quotes?.length > 0 ? (
        <div>
          <Button
            variant="secondary"
            onClick={() => setFilterModalOpen(true)}
            className="w-full flex items-center gap-1"
          >
            <span>Filter Tariffs</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
              />
            </svg>
          </Button>

          <div className="mt-6 mb-8">
            {filters?.onlyShowSwitchable ? (
              <h3 className="text-md font-bold leading-[1.5] text-center">
                {interpolate(EN.quotes.showing_quotes_length, {
                  length: filteredQuotes?.length ?? 0,
                })}
              </h3>
            ) : (
              <h3 className="text-md font-bold leading-[1.5] text-center">
                {interpolate(EN.quotes.non_switchable_showing_quotes_length, {
                  length: filteredQuotes?.length ?? 0,
                })}
              </h3>
            )}
          </div>

          <ul role="list" className="space-y-8">
            {filteredQuotes?.map((tariff) => (
              <li key={tariff.priceId}>
                <TariffPreview tariff={tariff} />
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          <LoadingCard />
          <LoadingCard />
        </div>
      )}

      <FilterTariffsModal
        open={filterModalOpen}
        setOpen={setFilterModalOpen}
        filters={filters}
        setFilters={setFilters}
      />

      <PriceCapInfoModal
        showInfoModal={showInfoModal}
        setShowInfoModal={setShowInfoModal}
      />

      <InfoModal
        open={showHowSavingsCalculatedModal}
        setOpen={setShowHowSavingsCalculatedModal}
        title={EN.quotes.how_was_calculated_text}
        body={
          <span className="space-y-4">
            {EN.quotes.how_was_calculated_answer}
          </span>
        }
      />
    </Container>
  );
};

export default Quotes;
